import { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Store } from 'components/common/types/Store.types';
import {
  Creative,
  CreativesFilters,
  CreativesListItem,
  CreativesRequestData,
  CreativeStatus,
  PreparedCreativesListItem,
} from 'components/common/types/Creative.types';
import { getStringFromObjectsList } from 'utils/formatters';
import { cancelFunctions } from 'utils/cancelFunctions';
import { usePageFilters } from 'customHooks/usePageFilters';
import { fetchCreatives } from 'modules/api/content';
import { setCreativesIdsList, updatePendingCreativesCount } from 'store/pages/contentManagement/creatives/reducer';
import { getPendingCreativeCount } from 'modules/api/creativeNotifications';
import { parseCreativesPreFilters } from './utils/parseCreativesPreFilters';
import { convertDateToUTC } from './utils/convertDateToUTC';
import { mapStatusFilter } from './utils/mapStatusFilter';
import Creatives from './Creatives';
import Filters from './Filters';
import { FetchCreativesParams, FetchCreativesResponse } from './Creatives/Creatives.types';

const Main = (): React.ReactElement => {
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const sourceRef = useRef(searchParams.get('source'));
  const { filters } = usePageFilters<CreativesFilters>('content-management/creatives', parseCreativesPreFilters);
  const [bulkSelectedCreatives, setBulkSelectedCreatives] = useState<Creative[]>([]);
  const environmentId = useSelector((state: Store) => state.environment.environmentId);
  const timezoneCode = useSelector((state: Store) => state.header.timezone.code);

  const mapFiltersToRequestData = (filterData: CreativesFilters): CreativesRequestData => {
    const statusFilter = mapStatusFilter(filterData);

    return {
      ...(statusFilter ? { statusFilter } : filterData.status),
      type: filterData?.creativeType,
      dspName: filterData.dsp?.name,
      productFormatName: filterData.productFormat?.name,
      externalIdContains: filterData?.externalId,
      width: filterData?.width,
      height: filterData?.height,
      dealId: filterData.dealIds?.map((deal) => deal.code).join(','),
      advertiserCode: filterData.advertiser?.code,
      brandCode: filterData.brand?.code,
      productCategoryCode: filterData.productCategory?.code,
      deleted: filterData.deleted ?? false,
      startUploadDate: filterData.startDate ? convertDateToUTC(filterData.startDate, timezoneCode) : undefined,
      endUploadDate: filterData.endDate ? convertDateToUTC(filterData.endDate, timezoneCode) : undefined,
      externalAdvertiserName: filterData.externalAdvertiserName,
      clickThroughUrl: filterData.clickThroughUrl,
    };
  };

  const handleExpiredDate = (deletedDate?: string | null): Date | null => {
    if (!deletedDate) return null;

    const date = new Date(deletedDate);
    date.setDate(date.getDate() + 30);

    return date;
  };

  const handleIsExpiredCreative = (expireData: Date): boolean => new Date() > expireData;

  const prepareCreativesList = (creatives: CreativesListItem[]): PreparedCreativesListItem[] => {
    return creatives.map((creative) => {
      const currentMarket = creative.market.find((market) => market.environment === environmentId);

      const expireDate = handleExpiredDate(currentMarket?.deletedAt);
      const isExpired = !expireDate || handleIsExpiredCreative(expireDate);

      return {
        ...creative,
        id: String(creative.id),
        environment: currentMarket?.environment,
        statusCode: currentMarket?.moderation[0]?.status ?? CreativeStatus.PENDING,
        dspName: creative?.dsp?.name,
        uploadedBy: creative.uploadedBy?.name,
        advertiser: currentMarket?.commercial?.advertiser?.name,
        brand: currentMarket?.commercial?.brand?.name,
        productCategory: currentMarket?.commercial?.productCategory?.name,
        externalAgencyId: creative?.externalAgencyId,
        externalAdvertiserName: creative?.externalAdvertiserName,
        clickThroughUrl: creative?.clickThroughUrl,
        formatName: getStringFromObjectsList(currentMarket?.productFormat),
        updatedBy: currentMarket?.moderation[0]?.updatedBy?.name,
        updated: currentMarket?.moderation[0]?.updateStamp,
        deletedAt: currentMarket?.deletedAt,
        expiredAt: expireDate?.toISOString(),
        agency: currentMarket?.agency?.name,
        specialist: currentMarket?.specialist?.name,
        isExpired,
      };
    }) as PreparedCreativesListItem[];
  };

  const onFetchCreatives = useCallback(
    async (extraQuery: FetchCreativesParams): Promise<FetchCreativesResponse> => {
      const source = sourceRef.current;
      sourceRef.current = null;

      const { content, totalElements } = await fetchCreatives(cancelFunctions, {
        ...mapFiltersToRequestData(filters as CreativesFilters),
        ...extraQuery,
        environment: environmentId,
        ...(source ? { source } : {}),
      });

      const preparedCreatives = prepareCreativesList(content);

      dispatch(setCreativesIdsList(preparedCreatives));

      return {
        content: preparedCreatives,
        totalElements,
      };
    },
    [environmentId, filters, timezoneCode],
  );

  const getNumberOfPendingCreatives = useCallback(async (): Promise<void> => {
    try {
      const data = await getPendingCreativeCount(environmentId, cancelFunctions);

      if (data === undefined || data === null) return;
      dispatch(updatePendingCreativesCount(data));
      // eslint-disable-next-line no-empty
    } catch {}
  }, [environmentId]);

  useEffect(() => {
    getNumberOfPendingCreatives();
  }, []);

  return (
    <>
      <div className="mb-5">
        <Filters />
      </div>
      <Creatives
        onFetchCreatives={onFetchCreatives}
        filterData={filters as CreativesFilters}
        setSelectedCreatives={setBulkSelectedCreatives}
        isCreativesSelected={bulkSelectedCreatives.length > 1 && bulkSelectedCreatives.length <= 100}
      />
    </>
  );
};

export default Main;
